import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { DeleteBuildType } from '@portal-core/builds/enums/delete-build-type.enum';
import { ProcessBuildType } from '@portal-core/builds/enums/process-build-type.enum';
import { BuildLanguageBranch } from '@portal-core/builds/models/build-language-branch.model';
import { BuildSchedule, BuildScheduleSaveModel } from '@portal-core/builds/models/build-schedule.model';
import { Build } from '@portal-core/builds/models/build.model';
import { DeleteBuildsResult } from '@portal-core/builds/models/delete-builds-result.model';
import { BuildOperationType } from '@portal-core/builds/types/build-operation-type.type';
import { AccessMap, AccessRequest } from '@portal-core/data/types/access-request.type';
import { LogPrepareResponse } from '@portal-core/live-log/models/log-prepare-response.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable } from 'rxjs';

@Service()
export class BuildsApiService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  @SharePendingRequest()
  getBuild$(buildId: number): Observable<Build> {
    return this.http.get<Build>(`${this.apiService.centralApiBaseUri}/api/Builds/${buildId}`);
  }

  @SharePendingRequest()
  getAccess$(request: AccessRequest<Build, BuildOperationType>): Observable<AccessMap<BuildOperationType>> {
    return this.http.post<AccessMap<BuildOperationType>>(`${this.apiService.centralApiBaseUri}/api/Builds/Access`, request);
  }

  @SharePendingRequest()
  getBuildsByIds$(buildIds: number[]): Observable<Build[]> {
    return this.http.post<Build[]>(`${this.apiService.centralApiBaseUri}/api/Builds/getBatchBuildsByIds`, buildIds);
  }

  @SharePendingRequest()
  getBuildByTargetPath$(projectId: number, branchName: string, targetPath: string): Observable<Build> {
    return this.http.get<Build>(`${this.apiService.centralApiBaseUri}/api/Builds/getBuildByTargetPath/${projectId}?targetPath=${encodeURIComponent(targetPath)}&branchName=${encodeURIComponent(branchName)}`);
  }

  @SharePendingRequest()
  getBuildByProjectIdAndPath$(projectId: number, targetPath: string): Observable<Build> {
    return this.http.get<Build>(`${this.apiService.centralApiBaseUri}/api/Builds/getBuildByProjectIdAndPath/${projectId}?targetPath=${encodeURIComponent(targetPath)}`);
  }

  @SharePendingRequest()
  getScheduledBuildsGridByProjectId$(projectId: number, filterModel: PageFilter): Observable<Page<BuildSchedule>> {
    return this.http.post<Page<BuildSchedule>>(`${this.apiService.centralApiBaseUri}/api/Builds/getScheduleBuildsGrid/${projectId}`, filterModel);
  }

  @SharePendingRequest()
  getBuildsPage$(filterModel: PageFilter): Observable<Page<Build>> {
    return this.http.post<Page<Build>>(`${this.apiService.centralApiBaseUri}/api/Builds/AdminGrid`, filterModel);
  }

  @SharePendingRequest()
  getBuildsPageByProjectId$(projectId: number, filterModel: PageFilter): Observable<Page<Build>> {
    return this.http.post<Page<Build>>(`${this.apiService.centralApiBaseUri}/api/builds/BuildsGrid/${projectId}`, filterModel);
  }

  bulkDeleteBuilds$(projectId: number, buildIds: number[]): Observable<MadCloudResult<void>> {
    return this.http.post<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/builds/bulkDeleteBuilds/${projectId}`, buildIds);
  }

  deleteBuildsInProject$(projectId: number, deleteBuildType: DeleteBuildType): Observable<MadCloudResult<DeleteBuildsResult>> {
    return this.http.delete<MadCloudResult<DeleteBuildsResult>>(`${this.apiService.centralApiBaseUri}/api/builds/${projectId}/deleteBuildsInProject/${deleteBuildType}`);
  }

  @SharePendingRequest()
  createBuild$(projectId: number, targetPath: string, branchName: string, description: string, buildLanguageBranches: BuildLanguageBranch[], destinationFilePaths: string[]): Observable<Build> {
    return this.http.post<Build>(`${this.apiService.centralApiBaseUri}/api/builds/queueBuild`, {
      projectId,
      targetPath,
      branchName,
      description,
      buildLanguageBranches,
      destinationFilePaths
    });
  }

  processBuild$(buildId: number, processBuildType: ProcessBuildType): Observable<void> {
    const params = new HttpParams().set('processBuildType', processBuildType.toString());
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/builds/processBuild/${buildId}`, null, { params });
  }

  @SharePendingRequest()
  getDownloadBuildSasToken$(buildId: number): Observable<string> {
    return this.http.get<string>(`${this.apiService.centralApiBaseUri}/api/builds/downloadToken/${buildId}`);
  }

  getBuildScheduleById$(buildScheduleId: number): Observable<BuildSchedule> {
    return this.http.get<BuildSchedule>(`${this.apiService.centralApiBaseUri}/api/builds/schedule/${buildScheduleId}`);
  }

  getBuildLogStreamUrl(buildId: number): string {
    return `${this.apiService.centralApiBaseUri}/api/Builds/${buildId}/log/stream`;
  }

  prepareBuildLog$(buildId: number): Observable<LogPrepareResponse> {
    return this.http.post<LogPrepareResponse>(`${this.apiService.centralApiBaseUri}/api/Builds/${buildId}/log/prepare`, null);
  }

  setBuildAsKeep$(buildId: number, keep: boolean): Observable<MadCloudResult<void>> {
    if (keep) {
      return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/builds/SetKeepBuild/${buildId}`, null);
    } else {
      return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/builds/CancelKeepBuild/${buildId}`, null);
    }
  }

  setUserViewedBuild$(buildId: number): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/builds/userViewed/${buildId}`, null);
  }

  updateBuildSettings$(buildId: number, isKept: boolean, description: string): Observable<MadCloudResult<void>> {
    return this.http.post<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/builds/buildSettings/${buildId}`, { isKept, description });
  }

  upsertBuildSchedule$(projectId: number, buildSchedule: BuildScheduleSaveModel): Observable<BuildSchedule> {
    return this.http.post<BuildSchedule>(`${this.apiService.centralApiBaseUri}/api/builds/${projectId}/buildSchedule`, buildSchedule);
  }

  bulkDeleteBuildSchedulesById$(projectId: number, buildScheduleIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/builds/${projectId}/bulkDeleteBuildSchedule`, buildScheduleIds);
  }
}
